import { QueryClient } from '@tanstack/react-query'

// Config de la casa (Carpenter): retry 1, staleTime 30 s, sin refetch al enfocar.
// El 401 ya lo maneja apiFetch (logout global), así que aquí no se duplica.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // gcTime 24 h: SIN esto la persistencia sería decorativa. El default de
      // react-query son 5 min, así que una query sin observadores se recolecta y
      // el archivo en IndexedDB se vaciaría solo (lib/persist.ts).
      gcTime: 24 * 60 * 60 * 1000,
      // Sin red las queries quedan en fetchStatus 'paused', NO en 'error': la
      // pantalla conserva lo que ya tenía en vez de apagarse. refetchOnReconnect
      // queda en su default (true), así que al volver la red se refresca sola.
    },
  },
})

// Llaves de query centralizadas — los módulos las importan, no inventan strings.
export const qk = {
  me: ['me'] as const,
  prefs: ['prefs'] as const,
  team: ['team'] as const,
  cells: ['cells'] as const,
  projects: (filter?: string) => ['projects', filter ?? 'mine'] as const,
  // Búsqueda de proyectos CONTRA EL SERVIDOR. Llave aparte a propósito: qk.projects
  // la comparten dashboard, chat, mis-tareas, dock y búsqueda, y guarda la lista
  // completa; los resultados de una búsqueda no deben sobrescribirla.
  projectSearch: (q: string) => ['projectSearch', q] as const,
  project: (pid: string) => ['project', pid] as const,
  activity: (pid: string) => ['activity', pid] as const,
  boards: (pid: string) => ['boards', pid] as const,
  items: (pid: string, boardId: string) => ['items', pid, boardId] as const,
  myTasks: ['myTasks'] as const,
  // Mismo prefijo para invalidar pendientes; distinto resultado por límite.
  dashboardTasks: ['myTasks', 'dashboard'] as const,
  posts: (pid: string) => ['posts', pid] as const,
  post: (pid: string, poId: string) => ['post', pid, poId] as const,
  notes: (pid: string) => ['notes', pid] as const,
  note: (pid: string, ntId: string) => ['note', pid, ntId] as const,
  resources: (pid: string) => ['resources', pid] as const,
  // Archivos del proyecto (pestaña Archivos, paginada con useInfiniteQuery) y el
  // vistazo del home, que es una página corta con OTRA forma: llaves distintas
  // bajo el mismo prefijo, para que invalidar ['archivos', pid] alcance a las dos.
  // Ninguna se persiste (no están en PERSISTIBLES), y la URL firmada menos: caduca.
  archivos: (pid: string) => ['archivos', pid] as const,
  archivosVistazo: (pid: string) => ['archivos', pid, 'vistazo'] as const,
  archivoUrl: (pid: string, aid: string) => ['archivoUrl', pid, aid] as const,
  // Liga vieja de Basecamp → ruta de camp (/bc?u=).
  ligaBasecamp: (u: string) => ['ligaBasecamp', u] as const,
  timeline: (pid: string) => ['timeline', pid] as const,
  brief: (pid: string) => ['brief', pid] as const,
  // Las ingestas del brief (texto pegado, documento, nota de voz, transcript): el
  // panel de revisión y la tarjeta del brief miran la misma lista.
  ingestas: (pid: string) => ['briefIngestas', pid] as const,
  ingesta: (pid: string, iid: string) => ['briefIngestas', pid, iid] as const,
  // Las FUENTES del brief (nota de voz, PDF, texto) que cada ingesta combina.
  // Tampoco se persisten: son la lista viva de lo que se va a leer.
  fuentesBrief: (pid: string) => ['briefFuentes', pid] as const,
  // La Propuesta IA la leen DOS módulos (la herramienta y la ToolCard del home),
  // por eso vive aquí y no local: los dos tienen que guardar el MISMO sobre entero,
  // porque `generando` es tan dato como la versión actual y desenvolverlo dejaría a
  // la herramienta y a la fila del home leyendo formas distintas de la misma llave.
  //
  // NO HAY una llave `debrief` para este módulo, y su ausencia es la decisión: la
  // palabra nombra desde sep 2026 OTRO documento —la reescritura del brief—, que
  // usa `debriefDoc` más abajo. Dejar aquí la llave vieja habría vuelto a poner las
  // dos cosas bajo el mismo nombre, que es exactamente lo que esta ola vino a
  // partir. Una llave del caché no se migra: se cambia y el sobre se vuelve a pedir.
  propuesta: (pid: string) => ['propuesta', pid] as const,
  // Una VERSIÓN concreta (GET /propuesta/:prid). Con el pimpón creando versiones
  // seguido, sin esto nadie puede leer la versión a la que responde un turno.
  propuestaVersion: (pid: string, prid: string) => ['propuesta', pid, prid] as const,
  // El hilo del pimpón, por sección: cada sección se sondea sola mientras la IA
  // piensa, y una llave por documento invalidaría los cuatro hilos a la vez.
  propuestaTurnos: (pid: string, seccion: string) => ['propuestaTurnos', pid, seccion] as const,
  // Debrief DOCUMENTO (la reescritura del brief), no el módulo viejo.
  debriefDoc: (pid: string) => ['debriefDoc', pid] as const,
  debriefDocVersion: (pid: string, vid: string) => ['debriefDoc', pid, vid] as const,
  notasCreativas: (pid: string) => ['notasCreativas', pid] as const,
  notaCreativa: (pid: string, nid: string) => ['notasCreativas', pid, nid] as const,
  // Etapa visual y sus piezas.
  visuales: (pid: string) => ['visuales', pid] as const,
  etapaVisual: (pid: string, eid: string) => ['visuales', pid, eid] as const,
  // Fotos y medidas del lugar (lo carga 3D antes de los renders). NO va a la lista
  // de persistibles: trae ligas firmadas que caducan en una hora.
  recinto: (pid: string) => ['recinto', pid] as const,
  // Logo real, patrocinadores y talento del cliente (antes de las artes). Tampoco
  // se persiste: sus imágenes son ligas firmadas de una hora.
  marca: (pid: string) => ['marca', pid] as const,
  brandbooks: (pid: string) => ['brandbooks', pid] as const,
  brandbook: (pid: string, bid: string) => ['brandbooks', pid, bid] as const,
  // Un audio suelto: la grabadora lo sondea mientras se transcribe, y lo miran la
  // ingesta del brief, la nota de creatividad y el input de voz de la etapa visual.
  audio: (pid: string, aid: string) => ['audio', pid, aid] as const,
  notifs: ['notifs'] as const,
  reminders: ['reminders'] as const,
  links: (pid: string, kind: string) => ['links', pid, kind] as const,
  trash: ['trash'] as const,
}
